import { AlertTriangle, Info, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ADDITIVE_COPY } from './AdditiveBreakdown';

interface PushConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  goingToZero: Array<{ sku: string; locationName?: string; previousQty: number; newQty: number }>;
  totalChanges: number;
  isPending: boolean;
  mode: 'bulk' | 'single';
  isDryRun?: boolean;
  /**
   * 57.1-07: true when a pre-printed SKU in this push draws on blank stock, so a
   * composite (pre-printed + blanks available to sell) qty will be pushed.
   */
  hybridQtyEnabled?: boolean;
  /**
   * SKUs set to Always 0 (the Skip or Always 0 card) that this push writes as 0. The
   * preview leaves them out of `goingToZero` (0 is what was asked for, and
   * they skip the zero guards), so they are named on their own line instead.
   */
  alwaysZeroCount?: number;
}

const MAX_VISIBLE_SKUS = 50;

export function PushConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  goingToZero,
  totalChanges,
  isPending,
  mode,
  isDryRun,
  hybridQtyEnabled,
  alwaysZeroCount = 0,
}: PushConfirmDialogProps) {
  const { t } = useTranslation('inventory');
  const hasZeroStock = goingToZero.length > 0;
  const otherChanges = totalChanges - goingToZero.length;
  const visibleSkus = goingToZero.slice(0, MAX_VISIBLE_SKUS);
  const hiddenCount = goingToZero.length - visibleSkus.length;

  return (
    <Dialog open={open} onOpenChange={isPending ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'bulk' ? 'Push inventory to all locations?' : 'Push to all locations?'}
            {isDryRun && (
              <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                Dry Run
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {hasZeroStock
              ? 'Some SKUs will be set to zero stock across all Shopify locations.'
              : `${totalChanges} change${totalChanges !== 1 ? 's' : ''} will be pushed across all Shopify locations.`}
          </DialogDescription>
        </DialogHeader>

        {hybridQtyEnabled && (
          <div className="rounded-md border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/20 p-3 flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                Composite quantity for pre-printed products
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-400">
                {ADDITIVE_COPY.pushComposite}
              </p>
            </div>
          </div>
        )}

        {hasZeroStock && (
          <div className="space-y-3">
            <div className="rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    {goingToZero.length} SKU{goingToZero.length !== 1 ? 's' : ''} will be set to 0
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    These products will show as unavailable to customers until stock is restored.
                  </p>
                </div>
              </div>
            </div>

            <ScrollArea className="max-h-[180px]">
              <div className="space-y-1 pr-3">
                {visibleSkus.map((item, i) => (
                  <div
                    key={`${item.sku}-${item.locationName ?? i}`}
                    className="flex items-center justify-between rounded px-2 py-1 text-xs bg-muted/50"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono">{item.sku}</span>
                      {item.locationName && (
                        <span className="text-muted-foreground truncate">@ {item.locationName}</span>
                      )}
                    </div>
                    <span className="text-muted-foreground tabular-nums shrink-0">
                      {item.previousQty === 0 ? 'qty: 0' : `${item.previousQty} → 0`}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {hiddenCount > 0 && (
              <p className="text-xs text-muted-foreground">
                + {hiddenCount} more{mode === 'bulk' ? ', review the Approvals tab for the full list' : ''}
              </p>
            )}

            {otherChanges > 0 && (
              <p className="text-xs text-muted-foreground">
                {otherChanges} other change{otherChanges !== 1 ? 's' : ''} will proceed normally.
              </p>
            )}
          </div>
        )}

        {alwaysZeroCount > 0 && (
          <p className="text-xs text-muted-foreground">
            {t('stockSync.pushRules.pushConfirm.alwaysZero', { count: alwaysZeroCount })}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant={hasZeroStock ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                Pushing...
              </>
            ) : hasZeroStock ? (
              'Confirm Push'
            ) : (
              'Push to Shopify'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
